/**
 * Service construct for exposing applications
 * 
 * Provides 80/20 utility for creating Kubernetes Services with:
 * - ClusterIP (default), NodePort, or LoadBalancer types
 * - Automatic label selectors matching deployment
 * - Standard port configurations
 */

import type {
  Service,
  ServiceSpec,
  ServicePort,
} from '@tilt-ts/k8s-schemas/dist/src/generated/k8s/v1.31/core/manifest';

import { validateService } from '@tilt-ts/k8s-schemas/dist/src/generated/k8s/v1.31/core/manifest';

import { 
  createManifestWrapper, 
  createWrapperFromFile, 
  createWrapperFromString,
  type K8sManifestWrapper 
} from './k8s-manifest-wrapper';

export interface ServiceOptions {
  /** Kubernetes namespace (defaults to 'default') */
  namespace?: string;
  /** Service type (defaults to 'ClusterIP') */
  type?: 'ClusterIP' | 'NodePort' | 'LoadBalancer';
  /** Target port on the container (defaults to same as port) */
  targetPort?: number;
  /** Protocol (defaults to 'TCP') */
  protocol?: 'TCP' | 'UDP';
  /** Additional labels to merge with standard app labels */
  labels?: Record<string, string>;
  /** Additional ports beyond the primary port */
  additionalPorts?: Array<{
    name?: string;
    port: number;
    targetPort?: number;
    protocol?: 'TCP' | 'UDP';
  }>;
}

/**
 * Creates a Kubernetes Service for exposing an application
 * 
 * @param app - Application name (used for labels and selector)
 * @param port - Primary service port
 * @param opts - Optional configuration
 * @returns Complete Service manifest
 * 
 * @example
 * ```typescript
 * // Basic ClusterIP service
 * const svc = service('my-app', 8080);
 * 
 * // LoadBalancer with custom target port
 * const lb = service('my-app', 80, {
 *   type: 'LoadBalancer',
 *   targetPort: 8080
 * });
 * 
 * // Multi-port service
 * const multiSvc = service('my-app', 8080, {
 *   additionalPorts: [
 *     { name: 'metrics', port: 9090, targetPort: 9090 }
 *   ]
 * });
 * ```
 */
export function service(
  app: string,
  port: number,
  opts: ServiceOptions = {}
): K8sManifestWrapper<Service> {
  const {
    namespace = 'default',
    type = 'ClusterIP',
    targetPort = port,
    protocol = 'TCP',
    labels = {},
    additionalPorts = [],
  } = opts;

  // Standard app labels
  const standardLabels = {
    app,
    'app.kubernetes.io/name': app,
    'app.kubernetes.io/component': 'service',
    'app.kubernetes.io/part-of': app,
    ...labels,
  };

  // Primary service port
  const primaryPort: ServicePort = {
    ...(additionalPorts.length > 0 && { name: 'http' }),
    port,
    targetPort,
    protocol,
  };

  // Additional service ports
  const extraPorts: ServicePort[] = additionalPorts.map(p => ({
    ...(p.name && { name: p.name }),
    port: p.port,
    targetPort: p.targetPort || p.port,
    protocol: p.protocol || 'TCP',
  }));

  // All ports
  const allPorts = [primaryPort, ...extraPorts];

  // Service specification
  const spec: ServiceSpec = {
    type,
    ports: allPorts,
    selector: {
      app, // This matches the app label from deployment
    },
  };

  // Complete Service manifest
  const serviceData: Service = {
    apiVersion: 'v1',
    kind: 'Service',
    metadata: {
      name: app,
      namespace,
      labels: standardLabels,
    },
    spec,
  };

  return createManifestWrapper(serviceData, validateService);
}

// Static factory methods for loading services from files/strings
service.fromFile = (path: string) => {
  return createWrapperFromFile<Service>(path, validateService);
};

service.fromString = (content: string) => {
  return createWrapperFromString<Service>(content, validateService);
};